import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Table,
    Button,
    Spinner,
    Badge,
} from "reactstrap";
import { useParams } from "react-router-dom";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import { fetchAccountPlans } from "../../helpers/api/accountPlans";
import { fetchProperties } from "../../helpers/api/condominium";
import { fetchCondominiumBanksAll } from "../../helpers/api/banks";
import { fetchProviders } from "../../helpers/api/provider";
import { fetchReserve, fetchDebtsToPay, fetchFinances, fetchAccountEntityCodes } from "../../helpers/api/finance";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown, faFolder, faFileInvoiceDollar, faHome, faUniversity, faTruckLoading, faCoins, faTags } from "@fortawesome/free-solid-svg-icons";
import NumberFormat from "react-number-format";
import { fetchAllPages } from "../../helpers/api/pagination";

// El nivel de una cuenta se determina por la profundidad de su código (cantidad
// de segmentos separados por punto), no por su account_type: 1er nivel = Grupo
// (ej. "1"), 2do = Subgrupo (ej. "1.1"), 3er = Cuenta de Mayor (ej. "1.1.1"),
// 4to en adelante = Subcuenta (ej. "1.1.2.01", "1.1.2.01-08").
const getAccountLevel = (code) => {
    const depth = (code || "").split(".").length;
    if (depth <= 1) return { name: "Grupo", color: "soft-primary" };
    if (depth === 2) return { name: "Subgrupo", color: "soft-info" };
    if (depth === 3) return { name: "Cuenta de Mayor", color: "soft-warning" };
    return { name: "Subcuenta", color: "soft-success" };
};

const AuxiliaryRow = ({ name, code, type, depth }) => {
    const icons = {
        property: faHome,
        bank: faUniversity,
        provider: faTruckLoading,
        debt: faFileInvoiceDollar,
        fund: faCoins,
        expense: faFileInvoiceDollar
    };

    return (
        <tr className="bg-soft-light italic">
            <td className="align-middle" style={{ paddingLeft: `${depth * 30 + 40}px` }}>
                <div className="d-flex align-items-center text-500">
                    <FontAwesomeIcon icon={icons[type]} className={`mr-2 fs--1 ${type === 'property' ? 'text-primary' : type === 'bank' ? 'text-success' : type === 'debt' ? 'text-danger' : 'text-info'}`} />
                    <span className="mr-2 fs--1 text-700">{code}</span>
                    <span className="fs--1">{name}</span>
                </div>
            </td>
            <td className="text-center italic text-400 fs--1 align-middle">
                <Badge color={getAccountLevel(code).color} className="fs--2">{getAccountLevel(code).name}</Badge>
            </td>
        </tr>
    );
};

const AccountPlanRow = ({ plan, depth = 0, auxiliaries = {} }) => {
    const [isOpen, setIsOpen] = useState(depth < 2);
    const hasChildren = plan.children && plan.children.length > 0;
    const isTitle = plan.account_type === 'title';

    const nameLower = plan.name.toLowerCase();

    // Lógica dinámica avanzada
    const isBanksAccount = nameLower.includes('banco') && plan.account_type === 'movement';
    const isPropertiesAccount = (nameLower.includes('cobrar') || nameLower.includes('propietario')) && !nameLower.includes('pagar') && plan.account_type === 'movement';
    const isFundsAccount = (nameLower.includes('reserva') || nameLower.includes('fondo')) && plan.account_type === 'movement';

    // Categorización de Cuentas por Pagar (Pasivos)
    const isPayablesAccount = (nameLower.includes('pagar') || nameLower.includes('pasivo')) && !nameLower.includes('cobrar') && plan.account_type === 'movement';
    const isServicePayable = isPayablesAccount && (nameLower.includes('servicio') || nameLower.includes('public'));
    const isMaintenancePayable = isPayablesAccount && nameLower.includes('mantenimiento');

    const isExpensesAccount = (plan.code.startsWith('6') || plan.code.startsWith('5')) && plan.account_type === 'movement';
    const isIncomeAccount = (plan.code.startsWith('4')) && plan.account_type === 'movement';
    const isMonthlyDuesAccount = plan.code === '4.1.01-01' || plan.code === '4.1' || nameLower.includes('cuotas del condominio mensual') || nameLower.includes('ingresos por condominio');

    const isOtherAccount = nameLower.includes('otros') || nameLower.includes('varios') || nameLower.includes('vargas') || nameLower.includes('comunes');

    let currentAuxiliaries = [];
    if (isBanksAccount) currentAuxiliaries = auxiliaries.banks;
    else if (isPropertiesAccount) {
        // Restaurar Cuentas por Cobrar (debts_to_pay)
        currentAuxiliaries = auxiliaries.properties;
    }
    else if (isFundsAccount) currentAuxiliaries = auxiliaries.funds;
    else if (isIncomeAccount) {
        if (isMonthlyDuesAccount) {
            currentAuxiliaries = auxiliaries.propertyPayments;
        } else {
            currentAuxiliaries = auxiliaries.incomes.filter(inc => inc.account_plan_id === plan.id);
        }
    }
    else if (isExpensesAccount) {
        currentAuxiliaries = auxiliaries.finances.flatMap(f =>
            (f.sub_expenses || []).filter(sub => sub.account_plan?.id === plan.id)
                .map(sub => ({
                    ...sub,
                    expenseName: sub.name || f.name,
                    amount: sub.value,
                    areaName: sub.accounting_area?.name || 'General',
                    areaId: sub.accounting_area?.id ?? null,
                    type: 'expense'
                }))
        );
    } else if (isPayablesAccount) {
        // 1. Vinculados por ID exacto
        const directDebts = auxiliaries.debts.filter(d =>
            d.finance?.sub_expenses?.some(sub => sub.id === d.value_id && sub.account_plan?.id === plan.id)
        );

        // 2. Fallback: Si es la cuenta general de Proveedores (como 2.1.1.01) y no hay directos, mostrar todo
        const isGeneralPayable = nameLower.includes('proveedor') || plan.code === '2.1.1.01';

        const matchingDebts = directDebts.length > 0 ? directDebts : (isGeneralPayable ? auxiliaries.debts : []);

        // Una subcuenta por proveedor, no por transacción: varios gastos del mismo
        // proveedor comparten un único código estable (igual que en el Diario y el Mayor),
        // en vez de que cada gasto le asigne un número distinto a la misma entidad.
        const seenProviders = new Map();
        matchingDebts.forEach(d => {
            const providerId = d.finance?.provider?.id ?? null;
            const key = providerId ?? `name:${d.finance?.provider_name || d.name || d.description || 'Proveedor'}`;
            if (!seenProviders.has(key)) {
                seenProviders.set(key, {
                    providerId,
                    name: d.finance?.provider?.name || d.finance?.provider_name || d.name || d.description || 'Proveedor'
                });
            }
        });
        currentAuxiliaries = Array.from(seenProviders.values());
    }

    const showAuxiliaries = currentAuxiliaries?.length > 0;

    return (
        <>
            <tr key={plan.id}>
                <td className="align-middle" style={{ paddingLeft: `${depth * 30 + 12}px` }}>
                    <div className="d-flex align-items-center">
                        {(hasChildren || showAuxiliaries) ? (
                            <FontAwesomeIcon
                                icon={isOpen ? faChevronDown : faChevronRight}
                                className="mr-2 cursor-pointer text-600"
                                onClick={() => setIsOpen(!isOpen)}
                                style={{ width: '12px' }}
                            />
                        ) : (
                            <div className="mr-2" style={{ width: '12px' }} />
                        )}
                        <FontAwesomeIcon
                            icon={isTitle ? faFolder : faFileInvoiceDollar}
                            className={`mr-2 ${isTitle ? 'text-primary' : 'text-success'}`}
                        />
                        <span className={`mr-2 font-weight-bold text-700`}>{plan.code}</span>
                        <span className={`${isTitle ? 'font-weight-bold text-800' : 'text-700'}`}>{plan.name}</span>
                    </div>
                </td>
                <td className="align-middle text-center">
                    <Badge color={getAccountLevel(plan.code).color}>
                        {getAccountLevel(plan.code).name}
                    </Badge>
                </td>
            </tr>
            {isOpen && (
                <>
                    {hasChildren && plan.children
                        .filter(child => {
                            const cName = child.name.toLowerCase();
                            return !cName.includes('otras cuentas por pagar') && !cName.includes('otros ingresos');
                        })
                        .map(child => (
                            <AccountPlanRow
                                key={child.id}
                                plan={child}
                                depth={depth + 1}
                                auxiliaries={auxiliaries}
                            />
                        ))}

                    {(() => {
                        if (isExpensesAccount) {
                            // Agrupar por Área Contable (Mantenimiento, etc), por id en vez de por
                            // nombre para no mezclar áreas distintas que compartan el mismo texto.
                            const groupedByArea = currentAuxiliaries.reduce((acc, item) => {
                                const key = item.areaId ?? `name:${item.areaName}`;
                                if (!acc[key]) acc[key] = { areaName: item.areaName, areaId: item.areaId, items: [] };
                                acc[key].items.push(item);
                                return acc;
                            }, {});

                            return Object.entries(groupedByArea).map(([key, group], areaIdx) => {
                                const { areaName, areaId, items } = group;
                                // Código estable por área (misma fuente que el Diario y el Mayor);
                                // si el área no tiene id (ej. gastos sin área asignada, bucket
                                // "General"), cae al correlativo local como respaldo.
                                const areaCode = (areaId && auxiliaries.accountingAreaCodes?.[areaId])
                                    || `${plan.code}.${(areaIdx + 1).toString().padStart(2, '0')}`;

                                return (
                                    <React.Fragment key={`${plan.id}-area-${key}`}>
                                        <tr className="bg-soft-info italic">
                                            <td className="align-middle" style={{ paddingLeft: `${(depth + 1) * 30 + 12}px` }}>
                                                <div className="d-flex align-items-center text-info fs--1 font-weight-bold">
                                                    <FontAwesomeIcon icon={faTags} className="mr-2" />
                                                    <span className="mr-2 text-700">{areaCode}</span>
                                                    <span>{areaName}</span>
                                                </div>
                                            </td>
                                            <td className="text-center italic text-400 fs--1">-</td>
                                        </tr>
                                        {items.map((item, itemIdx) => (
                                            <AuxiliaryRow
                                                key={`${plan.id}-exp-${itemIdx}`}
                                                name={item.expenseName}
                                                code={`${areaCode}.${(itemIdx + 1).toString().padStart(3, '0')}`}
                                                type="expense"
                                                depth={depth + 2}
                                            />
                                        ))}
                                    </React.Fragment>
                                );
                            });
                        }

                        const auxiliaryItems = currentAuxiliaries.map((aux, idx) => {
                            let name = "";
                            let code = "";
                            let type = "";

                            if (isPropertiesAccount) {
                                const person = aux.user?.data;
                                name = person ? `${person.firstname} ${person.lastname}` : `Propiedad ${aux.property}`;
                                // Código calculado en el backend (misma fuente que el Diario y el Mayor);
                                // si por algo faltara, se cae al correlativo local como respaldo.
                                code = auxiliaries.propertyCodes?.[aux.id] || `${plan.code}-${(idx + 1).toString().padStart(2, '0')}`;
                                type = "property";
                            } else if (isBanksAccount) {
                                name = aux.name || aux.bank?.name;
                                code = `${plan.code}.${(idx + 1).toString().padStart(3, '0')}`;
                                type = "bank";
                            } else if (isPayablesAccount) {
                                name = aux.name;
                                // Proveedor sin registrar (solo nombre de texto libre en el gasto): no hay
                                // una entidad estable a la cual asignarle número, así que cae en el código
                                // genérico de la cuenta madre — mismo criterio que usa el backend, y el
                                // código en sí viene calculado ahí (misma fuente que el Diario y el Mayor).
                                code = aux.providerId != null ? (auxiliaries.providerCodes?.[aux.providerId] || plan.code) : plan.code;
                                type = "debt";
                            } else if (isFundsAccount) {
                                name = aux.name || aux.description;
                                // Código calculado en el backend (misma fuente que el Diario y el Mayor);
                                // si por algo faltara, se cae al correlativo local como respaldo.
                                code = auxiliaries.reserveFundCodes?.[aux.id] || `${plan.code}.${(idx + 1).toString().padStart(3, '0')}`;
                                type = "fund";
                            } else if (isIncomeAccount && isMonthlyDuesAccount) {
                                const prop = aux.property?.data || aux.property;
                                const person = prop?.user?.data || prop?.auth_credential?.people?.[0];
                                name = person ? `${person.firstname} ${person.lastname}` : `Pago Inmueble ${prop?.property || ''}`;
                                code = `${plan.code}-${(idx + 1).toString().padStart(3, '0')}`;
                                type = "property";
                            }

                            if (!name) return null;

                            return (
                                <AuxiliaryRow
                                    key={`${plan.id}-aux-${idx}`}
                                    name={name}
                                    code={code}
                                    type={type}
                                    depth={depth + 1}
                                />
                            );
                        });

                        if (auxiliaryItems.length > 10) {
                            return (
                                <tr className="border-0">
                                    <td colSpan="2" className="p-0 border-0">
                                        <div className="custom-scrollbar" style={{
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f9fafd',
                                            borderLeft: '4px solid #e3e6f0'
                                        }}>
                                            <Table borderless size="sm" className="mb-0">
                                                <tbody>
                                                    {auxiliaryItems}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </td>
                                </tr>
                            );
                        }

                        return auxiliaryItems;
                    })()}
                </>
            )}
        </>
    );
};

const AccountPlansList = () => {
    const { id } = useParams(); // condominiumId
    const [plans, setPlans] = useState([]);
    const [auxiliaries, setAuxiliaries] = useState({
        properties: [],
        banks: [],
        debts: [],
        funds: [],
        finances: [],
        incomes: [],
        propertyPayments: [],
        providers: [],
        propertyCodes: {},
        providerCodes: {},
        accountingAreaCodes: {},
        reserveFundCodes: {}
    });
    const [loading, setLoading] = useState(true);

    const getPlans = async () => {
        setLoading(true);
        try {
            const [plansRes, propsRes, banksRes, debtsRes, fundsRes, financesRes, providersRes, entityCodesRes] = await Promise.all([
                fetchAccountPlans(id),
                fetchAllPages((page, pageSize) => fetchProperties(page, pageSize, { condominiumId: id })),
                fetchCondominiumBanksAll({ condominiumId: id }),
                fetchAllPages((page, pageSize) => fetchDebtsToPay(page, pageSize, { condominiumId: id })),
                fetchReserve(id),
                fetchFinances({ condominiumId: id }),
                fetchAllPages((page, pageSize) => fetchProviders(page, pageSize, { condominiumId: id })),
                fetchAccountEntityCodes(id)
            ]);

            const unwrap = (res) => {
                if (!res) return [];
                if (Array.isArray(res)) return res;
                if (res.data && Array.isArray(res.data)) return res.data;
                if (res.data && res.data.data && Array.isArray(res.data.data)) return res.data.data;
                return [];
            };

            setPlans(unwrap(plansRes));

            // fetchFinances (getCondominiumfinance) devuelve un objeto con múltiples campos
            const financesData = financesRes?.finances || [];
            const debtsToPayData = financesRes?.debtsToPay || [];
            const incomesData = financesRes?.responseIncome || [];
            const propertyPaymentsData = financesRes?.propertyPayments || [];

            setAuxiliaries({
                properties: unwrap(propsRes),
                banks: unwrap(banksRes),
                debts: debtsToPayData.length > 0 ? debtsToPayData : unwrap(debtsRes),
                funds: unwrap(fundsRes),
                finances: financesData,
                incomes: incomesData,
                propertyPayments: propertyPaymentsData,
                providers: unwrap(providersRes),
                // Código de subcuenta por propiedad/proveedor: viene calculado del backend
                // (misma fuente que usan el Diario y el Mayor), no se recalcula acá.
                propertyCodes: entityCodesRes?.properties || {},
                providerCodes: entityCodesRes?.providers || {},
                accountingAreaCodes: entityCodesRes?.accountingAreas || {},
                reserveFundCodes: entityCodesRes?.reserveFunds || {}
            });
        } catch (error) {
            handleErrorMessage(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Plan de Cuentas | Homelan";
        getPlans();
    }, [id]);

    return (
        <>
            <PageHeader
                title="Plan de Cuentas"
                description="Vista jerárquica integrada con propietarios, bancos y proveedores."
                className="mb-3"
            />
            <Card className="shadow-none border">
                <FalconCardHeader title="Estructura Contable Consolidada" light />
                <CardBody className="p-0">
                    {loading ? (
                        <div className="text-center p-4">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <Table responsive hover className="mb-0 overflow-hidden">
                            <thead className="bg-200">
                                <tr>
                                    <th className="text-900">Cuenta y Descripción</th>
                                    <th className="text-900 text-center">Nivel</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plans.length > 0 ? (
                                    plans.map((plan) => (
                                        <AccountPlanRow
                                            key={plan.id}
                                            plan={plan}
                                            auxiliaries={auxiliaries}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="text-center p-5 text-warning border-0">
                                            <FontAwesomeIcon icon={faFolder} className="fs-3 mb-3 d-block mx-auto opacity-25" />
                                            No se ha inicializado el Plan de Cuentas.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

export default AccountPlansList;
